import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  ChallengeApiRecord,
  ChallengePathAvailability,
  UpdateChallengeRewardRequest,
  UpdateChallengeRewardResponse,
} from "../../types/gamePass";
import type { ChallengeDraft } from "../../components/GamePass/CreateSeason/createSeasonData";
import { getPrivateInstance } from "../_privateInstance";
import { mapChallengeRewardsToRequest } from "./challengeRewards";
import { unwrapGamePassEntity } from "./responseMappers";

const buildUpdateChallengeRewardUrl = (challengeId: string): string =>
  API_ROUTES.UPDATE_CHALLENGE_REWARD.replace("{challenge_id}", challengeId);

/** Convert the challenge editor's reward rows into the dedicated reward PUT
 *  body. The editor always carries the full reward list, so the update sends
 *  it as an authoritative replacement. */
export const mapChallengeDraftToUpdateChallengeRewardRequest = (
  rewards: ChallengeDraft["rewards"],
  pathAvailability: ChallengePathAvailability,
): UpdateChallengeRewardRequest => ({
  rewards: mapChallengeRewardsToRequest(rewards, pathAvailability),
});

export type UpdateChallengeRewardVariables = {
  challengeId: string;
  seasonId?: string | null;
  body: UpdateChallengeRewardRequest;
};

const putUpdateChallengeReward = async ({
  challengeId,
  body,
}: UpdateChallengeRewardVariables): Promise<ChallengeApiRecord> => {
  const response = await getPrivateInstance().put<UpdateChallengeRewardResponse>(
    buildUpdateChallengeRewardUrl(challengeId),
    body,
  );
  return unwrapGamePassEntity(response.data, "challenge");
};

export const useUpdateChallengeReward = (): UseMutationResult<
  ChallengeApiRecord,
  Error,
  UpdateChallengeRewardVariables
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "challenges", "update-reward"],
    mutationFn: putUpdateChallengeReward,
    onSuccess: (_challenge, variables) => {
      if (variables.seasonId) {
        queryClient.invalidateQueries({
          queryKey: ["game-pass", "challenges", "list", variables.seasonId],
        });
        queryClient.invalidateQueries({
          queryKey: ["game-pass", "seasons", "detail", variables.seasonId],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ["game-pass", "challenges", "list"],
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "seasons", "list"],
      });
    },
  });
};
